<template>
  <v-card :scrollable="true" class="pa-6" min-height="900px">
    <v-form @submit.prevent="handleSubmit" ref="formRef" lazy-validation>
      <!-- Survey Name -->
      <v-text-field
        v-model="form.name"
        label="اسم الاستبيان"
        :rules="[rules.required]"
        outlined
        dense
        :error-messages="errors.name ? [errors.name] : []"
      />

      <!-- Date Pickers -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="mt-5" v-for="(label, key) in dateLabels" :key="key">
          <v-text-field
            :label="label"
            :value="formatDateForUTCInput(form[key])"
            @update:model-value="(val) => (form[key] = val)"
            type="datetime-local"
            :full-width="true"
            :rules="[rules.required, ...getDateRules(key)]"
            :error-messages="errors[key] ? [errors[key]] : []"
            :disabled="isFieldDisabled(key)"
          ></v-text-field>
        </div>
      </div>

       <!-- Users count -->
      <div v-if="form.usersCount > 0" class="text-sm text-green-700 mt-4">
        ✅ {{ form.usersCount }} مستخدمين مسجلين
      </div>

      <!-- Upload Users -->
      <v-file-input
        label="تحميل جهات الاتصال (ملف Excel)"
        accept=".xlsx,.xls"
        prepend-icon="mdi-upload"
        v-model="form.users"
        dense
        outlined
       
        :error-messages="errors.users ? [errors.users] : []"
        class="mt-2"
      />

      <!-- Users count -->
      <div v-if="usersCount > 0" class="text-sm text-green-700 mt-2">
        ✅ {{ usersCount }} مستخدم تم تحميلهم من ملف Excel
      </div>

      <!-- Questions Section -->
      <h3 class="text-lg font-semibold mt-6 mb-2">الأسئلة</h3>
      <div
        :error-messages="errors.questions ? [errors.questions] : []"
        v-for="(question, index) in form.questions"
        :key="index"
        class="mb-4 border rounded p-4"
      >
        <v-text-field
          v-model="question.question"
          label="نص السؤال"
          :rules="[rules.required]"
          outlined
          dense
        />

        <v-select
          v-model="question.type"
          :items="questionTypes"
          item-title="title"
          item-value="value"
          label="نوع السؤال"
          :rules="[rules.required]"
          outlined
          dense
        />

        <div v-if="['radio', 'checkbox'].includes(question.type)">
          <v-combobox
            v-model="question.answers"
            :rules="[rules.required]"
            multiple
            chips
            label="الخيارات (افصل بينها بفاصلة أو اضغط Enter)"
            outlined
            dense
            :hide-no-data="true"
            :items="[]"
          />
        </div>

        <v-btn color="error" variant="text" @click="removeQuestion(index)" class="mt-2">
          حذف السؤال
        </v-btn>
      </div>

      <v-btn color="primary" variant="outlined" @click="addQuestion"> + إضافة سؤال </v-btn>

      <!-- Action Buttons -->
      <div class="d-flex justify-end mt-6">
        <v-btn text @click="$emit('cancel')">إلغاء</v-btn>
        <v-btn color="primary" type="submit" class="mr-2">
          {{ form._id ? 'تحديث الاستبيان' : 'إنشاء الاستبيان' }}
        </v-btn>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import { mapActions } from 'pinia'
import { useGlobalStore } from '../../stores/global'
import repository from '../../api/repository'
import momentTZ from 'moment-timezone'
// import moment from 'moment-timezone'
import * as XLSX from 'xlsx'

export default {
  props: {
    survey: {
      type: Object,
    },
  },
  data() {
    return {
      form: { ...this.survey },
      usersCount: 0, 
      dateMenus: {
        startDate: false,
        endDate: false,
        firstReminder: false,
        secondReminder: false,
      },
      dateLabels: {
        startDate: 'تاريخ البدء',
        endDate: 'تاريخ الانتهاء',
        firstReminder: 'التذكير الأول',
        secondReminder: 'التذكير الثاني',
      },
      questionTypes: [
        { title: 'تقييم (1-10)', value: 'rate' },
        { title: 'اختيار من متعدد', value: 'radio' },
      ],
      rules: {
        required: (v) => !!v || 'هذا الحقل مطلوب',
      },
      errors: {},
    }
  },

   watch: {
    'form.users'(newFile) {
      if (!newFile || newFile.length === 0) {
        this.usersCount = 0
        return
      }

      const file = newFile[0]
      const reader = new FileReader()

      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json(worksheet)

        this.usersCount = jsonData.length
      }

      reader.readAsArrayBuffer(file)
    },
  },

  methods: {
    ...mapActions(useGlobalStore, ['setAlertMessage']),

    getDateRules(key) {
      const rules = []

      const { startDate, endDate } = this.form

      switch (key) {
        case 'startDate':
          rules.push((v) => this.futureDate(v))
          break

        case 'endDate':
          rules.push((v) => this.endDateAfterStartDate(v, startDate))
          break

        case 'firstReminder':
        case 'secondReminder':
          rules.push((v) => this.reminderWithinRange(v, startDate, endDate))
          break
      }

      return rules
    },
    closeOpenDatePicker(key, action = 'toggle') {
      // First, close all
      Object.keys(this.dateMenus).forEach((k) => {
        if (k !== key) this.dateMenus[k] = false
      })

      // Then, optionally toggle the one passed
      if (action === 'toggle') {
        this.dateMenus[key] ^= true
      } else if (action === 'close') {
        this.dateMenus[key] = false
      }
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString()
    },
    addQuestion() {
      if(this.form.questions.length >= 10){
        this.setAlertMessage({
          display: true,
          text: 'يمكنك إضافة حتى 10 أسئلة كحد أقصى.',
          class: 'error',
        })
        return
      }
      this.form.questions.push({
        question: '',
        type: '',
        answers: [],
      })
    },
    removeQuestion(index) {
      this.form.questions.splice(index, 1)
    },
    handleFileUpload(file) {
      this.form.users = file
    },

    async handleSubmit() {
      this.errors = {} // clear old errors

      const validateName = this.validateName(this.form.name)
      if (validateName !== true) {
        this.errors.name = validateName
      }

      if(!this.form._id){
        const validateUsers = this.validateUsers(this.form.users)
        if (validateUsers !== true) {
          this.errors.users = validateUsers
        }
      }
     
      // Validate start_date
      const startDateValidation = this.futureDate(this.form.startDate)

      // Validate end_date
      const endDateValidation = this.endDateAfterStartDate(this.form.endDate, this.form.startDate)
      const firstReminderValidation = this.reminderWithinRange(
        this.form.firstReminder,
        this.form.startDate,
        this.form.endDate,
      )
      const secondReminderValidation = this.reminderWithinRange(
        this.form.secondReminder,
        this.form.startDate,
        this.form.endDate,
      )

      if (startDateValidation !== true) {
        this.errors.startDate = startDateValidation
      }

      if (endDateValidation !== true) {
        this.errors.endDate = endDateValidation
      }

      if (firstReminderValidation !== true) {
        this.errors.firstReminder = firstReminderValidation
      }

      if (secondReminderValidation !== true) {
        this.errors.secondReminder = secondReminderValidation
      }

      const questionValidation = this.validateQuestionCount(this.form.questions.length)
      if (questionValidation !== true) {
        this.errors.questions = questionValidation
      }

      if (Object.keys(this.errors).length > 0) {
        const allMessages = `<ul>${Object.values(this.errors)
          .map((msg) => `<li>${msg}</li>`)
          .join('')}</ul>`

        this.setAlertMessage({
          display: true,
          text: allMessages,
          class: 'error',
        })

        return
      }

      // Validation passed — proceed
      console.log('Form submitted successfully:', this.form)

      const form_data = new FormData()
      form_data.append('name', this.form.name)
      form_data.append(
        'startDate',
        momentTZ(this.form.startDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      )

      form_data.append(
        'endDate',
        momentTZ(this.form.endDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      )
      form_data.append(
        'firstReminder',
        momentTZ(this.form.firstReminder).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      )
      form_data.append(
        'secondReminder',
        momentTZ(this.form.secondReminder).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      )

      this.form.questions = this.form.questions.map((q) => {
        let answers = q.answers

        if (typeof answers === 'string') {
          answers = answers.split(',').map((s) => s.trim()).filter(Boolean)
        }

        if (Array.isArray(answers)) {
          answers = answers
            .flatMap((a) => String(a).split(','))
            .map((s) => s.trim())
            .filter(Boolean)

          answers = [...new Set(answers)]
        }

        return {
          ...q,
          answers,
        }
      })

      if (this.form.users) form_data.append('files', this.form.users[0])
      form_data.append('questions', JSON.stringify(this.form.questions))

      const config = {
        maxBodyLength: Infinity,
        keepExtensions: true,
        headers: {
          'content-type': 'multipart/form-data',
        },
      }

      let res = null;

      if(this.form._id)
        res = await repository.patchWithConfig(`surveys/${this.form._id}`, form_data, config)
      else
        res = await repository.postWithConfig('surveys', form_data, config)
      console.log('sendMessage', { res })
      this.$emit('saved', this.form)
    },
    validateQuestionCount(value) {
      if (value === null || value === '') return 'عدد الأسئلة مطلوب'
      if (isNaN(value)) return 'يجب أن يكون عدد الأسئلة رقماً'
      if (parseInt(value) < 1) return 'يجب أن يحتوي الاستبيان على سؤال واحد على الأقل'
      if (parseInt(value) > 10) return 'لا يمكن أن يزيد عدد الأسئلة عن 10 أسئلة'

      return true
    },

    validateUsers(value) {
      if (value === undefined || value === null || value === '') return 'يجب رفع ملف المستخدمين'
      return true
    },

    validateName(value) {
      if (value === undefined || value === null || value === '') return 'اسم الاستبيان مطلوب'
      return true
    },

    futureDate(value) {
      const selected = new Date(value)
      const today = new Date()

      return selected >= today || 'يجب أن يكون التاريخ اليوم أو في المستقبل'
    },

    endDateAfterStartDate(endDate, startDate) {
      if (!endDate || !startDate) return true

      const start = new Date(startDate)
      const end = new Date(endDate)

      return end > start || 'يجب أن يكون تاريخ الانتهاء بعد تاريخ البدء'
    },

    reminderWithinRange(reminder, startDate, endDate) {
      if (!reminder || !startDate || !endDate) return true

      const r = new Date(reminder)
      const s = new Date(startDate)
      const e = new Date(endDate)

      r.setHours(0, 0, 0, 0)
      s.setHours(0, 0, 0, 0)
      e.setHours(0, 0, 0, 0)

      return (r >= s && r <= e) || 'يجب أن يكون تاريخ التذكير بين تاريخ البدء وتاريخ الانتهاء'
    },

    formatDateForUTCInput(value) {
      if (!value) return null
      return momentTZ(value).format('YYYY-MM-DD HH:mm') // <-- required format
    },

    isFieldDisabled(key) {
  if (key === 'endDate') {
    return !this.form.startDate
  }

  if (['firstReminder', 'secondReminder'].includes(key)) {
    return !this.form.startDate || !this.form.endDate
  }

  return false
}

  },
}
</script>

<style scoped>
.v-combobox >>> .v-chip {
  margin: 2px;
}
</style>
